import { useRef, useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { calculateMouseInfluence } from './utils/mouseInteraction';

export function SpiralStars() {
  const starsRef = useRef<THREE.Points>(null);
  const positionsRef = useRef<Float32Array>(new Float32Array(15000 * 3));
  const colorsRef = useRef<Float32Array>(new Float32Array(15000 * 3));
  const sizesRef = useRef<Float32Array>(new Float32Array(15000));
  const originalPositionsRef = useRef<Float32Array>(new Float32Array(15000 * 3));
  const velocitiesRef = useRef<Float32Array>(new Float32Array(15000 * 3));
  const mousePosition = useRef(new THREE.Vector2());
  const { camera } = useThree();

  // Initialize star positions, colors, and sizes
  useMemo(() => {
    const starCount = 15000;
    
    for (let i = 0; i < starCount; i++) {
      const i3 = i * 3;
      
      const progress = i / starCount;
      const angle = progress * Math.PI * 2 * 3;
      const radius = 5 + (Math.pow(progress, 0.5) * 20);
      
      const arms = 4;
      const armAngle = ((i % arms) / arms) * Math.PI * 2;
      const spiralAngle = angle + armAngle;
      
      const randRadius = radius + (Math.random() - 0.5) * 5;
      const heightVariation = (Math.random() - 0.5) * 2;
      
      const x = Math.cos(spiralAngle) * randRadius;
      const y = heightVariation;
      const z = Math.sin(spiralAngle) * randRadius;
      
      positionsRef.current[i3] = x;
      positionsRef.current[i3 + 1] = y;
      positionsRef.current[i3 + 2] = z;
      
      originalPositionsRef.current[i3] = x;
      originalPositionsRef.current[i3 + 1] = y;
      originalPositionsRef.current[i3 + 2] = z;
      
      velocitiesRef.current[i3] = 0;
      velocitiesRef.current[i3 + 1] = 0;
      velocitiesRef.current[i3 + 2] = 0;
      
      const distanceFromCenter = Math.sqrt(x * x + y * y + z * z);
      const intensity = Math.pow(Math.max(0, 1 - (distanceFromCenter / 25)), 2);
      
      // Enhanced glow colors with slight variation
      const baseColor = Math.random();
      colorsRef.current[i3] = 0.6 * intensity * (0.9 + baseColor * 0.1);     // R
      colorsRef.current[i3 + 1] = 0.2 * intensity * (0.9 + baseColor * 0.1); // G
      colorsRef.current[i3 + 2] = 0.8 * intensity * (0.9 + baseColor * 0.1); // B

      // Vary star sizes for enhanced glow effect
      sizesRef.current[i] = (0.15 + Math.random() * 0.05) * (1 + intensity);
    }
  }, []);

  useFrame((state) => {
    if (!starsRef.current) return;

    mousePosition.current.set(
      state.mouse.x,
      state.mouse.y
    );

    const positions = positionsRef.current;
    const velocities = velocitiesRef.current;
    let needsUpdate = false;
    
    for (let i = 0; i < positions.length; i += 3) {
      const starPosition = new THREE.Vector3(
        positions[i],
        positions[i + 1],
        positions[i + 2]
      );
      
      const { displacement } = calculateMouseInfluence(
        mousePosition.current,
        starPosition,
        camera
      );
      
      const dampening = 0.92;
      const returnForce = 0.05;
      
      velocities[i] = velocities[i] * dampening + displacement.x - 
        (positions[i] - originalPositionsRef.current[i]) * returnForce;
      velocities[i + 1] = velocities[i + 1] * dampening + displacement.y - 
        (positions[i + 1] - originalPositionsRef.current[i + 1]) * returnForce;
      velocities[i + 2] = velocities[i + 2] * dampening + displacement.z - 
        (positions[i + 2] - originalPositionsRef.current[i + 2]) * returnForce;
      
      positions[i] += velocities[i];
      positions[i + 1] += velocities[i + 1];
      positions[i + 2] += velocities[i + 2];
      
      needsUpdate = true;
    }

    if (needsUpdate && starsRef.current) {
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.BufferAttribute(colorsRef.current, 3));
      geometry.setAttribute('size', new THREE.BufferAttribute(sizesRef.current, 1));
      
      starsRef.current.geometry.dispose();
      starsRef.current.geometry = geometry;
    }
  });

  // Custom shader material for enhanced glow
  const shaderMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      vertexShader: `
        attribute float size;
        varying vec3 vColor;
        varying float vDistance;
        
        void main() {
          vColor = color;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (300.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
          vDistance = 1.0 - min(1.0, -mvPosition.z / 50.0);
        }
      `,
      fragmentShader: `
        varying vec3 vColor;
        varying float vDistance;
        
        void main() {
          vec2 center = gl_PointCoord * 2.0 - 1.0;
          float dist = length(center);
          
          // Smooth circular glow
          float strength = 1.0 - smoothstep(0.0, 1.0, dist);
          strength = pow(strength, 1.5); // Adjust glow falloff
          
          // Add subtle inner core
          float core = 1.0 - smoothstep(0.0, 0.3, dist);
          strength += core * 0.5;
          
          // Distance-based fade for depth effect
          strength *= mix(1.0, 0.3, vDistance);
          
          gl_FragColor = vec4(vColor, strength * 0.8);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      vertexColors: true,
    });
  }, []);

  return (
    <points ref={starsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[positionsRef.current, 3]}
          count={positionsRef.current.length / 3}
          array={positionsRef.current}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          args={[colorsRef.current, 3]}
          count={colorsRef.current.length / 3}
          array={colorsRef.current}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-size"
          args={[sizesRef.current, 1]}
          count={sizesRef.current.length}
          array={sizesRef.current}
          itemSize={1}
        />
      </bufferGeometry>
      <primitive object={shaderMaterial} />
    </points>
  );
}